// checkout — the order page (canvas 3, "Checkout" artboard).
// Keys mirror ../pl/checkout.ts exactly.
//
// ⚠️ `consent.withdrawal` and `consent.terms` ARE NOT COPY. They are the text of
// the declarations stored in the `consents` table together with a document
// version, and they are the ONLY evidence in a chargeback or a dispute about the
// right of withdrawal.
// ANY change — even one character — in these two strings requires bumping
// `TERMS_VERSION` / `WITHDRAWAL_VERSION` in `app/billing/consents.py` IN THE SAME
// PR (P01 enforces it with a hash test — OBJECT INFO-P01-007, MGR ruling 19:20).
// Otherwise the evidence points at text the user never saw.
//
// The wording of `consent.withdrawal` is DELIBERATELY stronger than the first
// version P01 filed: it grounds the waiver in consent given BEFORE THE
// WITHDRAWAL PERIOD EXPIRES (art. 38(13) of the Polish Consumer Rights Act),
// not merely on "immediately". That is exactly the gap CRITIC raised as B-10(b);
// the canvas closes it.
//
// ⚠️ PROTECTED ZONE. Round B-R3 (rule 34) moved the currency word to "token"
// across the REST of this screen — heading, product row, summary — but NOT in the
// four consent statements (`consent.withdrawal`, `consent.terms`,
// `consent.withdrawalTopup`, `consent.termsTopup`). There the old word stands on
// purpose: it moves in one legal step together with `app/billing/consents.py`,
// the Terms and the Privacy Policy, and only then do the versions get bumped.
// A screen saying "tokens" above a consent saying "gems" is inconsistent; a
// consent that does not match the row stored in `consents` character for
// character has NO EVIDENCE behind it — and that is the costlier inconsistency.
//
// THE TOP-UP VARIANT (`*Topup`, D-020): the same screen also sells a gem pack,
// and a pack is NOT a subscription — "every month" and "renews automatically"
// are simply false there, and a false consent is worse than a missing one.
// Hence separate statements rather than one string with a condition in the code.
// `consent.withdrawalTopup` and `consent.termsTopup` are character-for-character
// identical to `CONSENT_TEXTS[...][TOPUP]` in `app/billing/consents.py`. Both of
// their factual claims were checked against the shipped Terms (Gems: packs do
// not expire; Refunds: an unused pack in full, a partly used one pro rata) and
// both hold, so P01's wording is adopted unchanged. That is not editorial
// laziness: identical text means no version bump and no drift between what the
// user read and what we stored as evidence.
export const checkout = {
  headingBare: 'Order',
  heading: 'Order - {plan} plan',
  headingTopup: 'Order - {gems} token pack',
  choose: {
    title: 'Choose what to buy',
    body: 'This address names neither a plan nor a token pack. Plans and packs are on the pricing page; your balance and current plan are under Billing.',
    pricing: 'See pricing',
    billing: 'Billing',
  },
  stale: {
    planTitle: 'We do not know that plan',
    planBody: 'The link may be out of date. Nothing was charged.',
    packTitle: 'We do not know that token pack',
    packBody: 'That pack is unavailable for your account, or the link is out of date. Nothing was charged.',
    currentPlans: 'Current plans',
    currentPacks: 'Current packs for your account',
    pricing: 'Choose from pricing',
  },
  refusal: {
    title: 'This plan cannot be bought right now',
    body: 'This is on our side, rather than yours. Nothing was charged, and trying again will not change it. Contact us or choose another plan.',
    contact: 'Contact us',
    nothingCharged: 'Nothing was charged.',
    otherPlan: 'Choose another plan',
  },
  summary: {
    item: 'EDIMI {plan}',
    itemDesc: 'Access to the software + {gems} tokens every month + {accounts}',
    itemTopup: '{gems} token pack',
    itemDescTopup: 'A one-time purchase. The tokens are valid for 24 months from purchase and stay on your account when your plan renews.',
    perMonth: 'per month',
    net: 'Net amount',
    vat: 'VAT {rate}% ({country})',
    dueToday: 'Due today',
    vatNote: 'The price includes VAT. Next charge on {date}, monthly after that.',
    // No "Next charge…". Announcing a further payment under a one-time pack is
    // a promise of a charge that will never happen.
    vatNoteOnce: 'The price includes VAT. A one-time payment - no renewal.',
  },
  payment: {
    // The sentence that removes the most common fear of a card form.
    stripeNote: 'Payments are handled by Stripe. We never see or store your card details.',
    descriptorLabel: 'On your card statement you will see',
    descriptor: 'EDIMI',
    connecting: 'Connecting to Stripe',
  },
  country: 'Billing country',
  countryPick: 'Choose a country',
  countryVat: 'The VAT rate depends on the billing country.',
  countryMissing: 'Without a country we cannot work out the VAT - pick one to see the amount due.',
  consent: {
    heading: 'Before you pay',
    lead: 'Both consents are required.',
    withdrawal: 'I request immediate access to the software and gems, before the 14-day withdrawal period expires. I acknowledge that I lose the right of withdrawal for the gems already used, and that the unused part will be settled pro rata.',
    terms: 'I accept the subscription terms: {price} incl. VAT every month, renewing automatically until cancelled, cancellable at any time in the customer portal. I accept the Terms and the Privacy Policy.',
    withdrawalTopup: 'I request immediate access to the gems I am buying, before the 14-day withdrawal period expires. I acknowledge that I lose the right of withdrawal for the gems already used, and that the unused part will be settled pro rata.',
    termsTopup: 'I accept the terms of this purchase: {price} incl. VAT, one time, no renewal. Purchased gems are valid for 24 months from purchase. I accept the Terms and the Privacy Policy.',
    termsLink: 'Terms',
    privacyLink: 'Privacy Policy',
  },
  submit: 'Pay {price} and start',
  submitBare: 'Pay and start',
  seller: 'Seller: JDG Dominik Łupicki',
  refundsLink: 'Refunds & cancellation',
  contactLink: 'Contact',
  priceChangeNote: 'We give you at least 7 days’ notice of any change to the price or the terms.',
}
